<template>
    <div
        class="bo-return-search"
        v-click-outside="vcoConfig"
    >
        <!-- SEARCH DROPDOWN -->
        <div class="searchbar rounded">
            <a
                class="btn-search"
                id="consumer_search_product_link"
            >
                <i
                    class="icon-search"
                    @click="searchIfNeeded"
                ></i>
            </a>
            <input
                type="text"
                id="dropdownSearchBar"
                data-toggle="dropdown"
                aria-haspopup="true"
                aria-expanded="false"
                class="search-input"
                :placeholder="generalTranslations.search ?? 'Search'"
                v-model="searchValue"
                @blur="searchIfNeeded"
                @keypress="checkKeyPress"
            />
            <div
                class="dropdown-menu dropdown-search-menu included"
                aria-labelledby="dropdownSearchBar"
                ref="dropdownSearchBar"
            >
                <div class="dropdown-search-filter">
                    <button
                        v-for="field in searchFields"
                        :key="field"
                        class="btn-rounded btn-primary included"
                        :class="isSearchFieldSelected(field) ? 'filter-select' : 'filter-unselect'"
                        @click="filterControl(field)"
                    >
                        {{ searchFieldLabel(field) }}
                    </button>
                </div>
                <bo-search-history
                    v-if="!searchValue.trim()"
                    :terms="searchHistory"
                    :editing="editingSearchHistory"
                    :max-rows="2"
                    :translations="headerTranslations"
                    @select="searchHistoryTerm"
                    @remove="removeSearchHistoryTerm"
                    @clear-all="clearSearchHistory"
                    @edit="editingSearchHistory = $event"
                ></bo-search-history>
                <div
                    v-else
                    class="dropdown-search-items"
                >
                    <div
                        v-for="searchResult in searchResults"
                        :key="searchResult.key"
                        class="dropdown-search-item"
                        @click="clickResult(searchResult)"
                    >
                        <div class="left-panel">
                            <span class="type">{{ searchResult.type }}</span>
                            <span class="name">{{ searchResult.name }}</span>
                            <span class="sku">{{ searchResult.sku }}</span>
                            <span
                                v-if="searchResult.barcode"
                                class="sku"
                                >{{ searchResult.barcode }}</span
                            >
                            <span
                                v-if="isSearchFieldSelected('description') && searchResult.description"
                                class="description"
                                >{{ searchResult.description }}</span
                            >
                            <span class="uom">{{ searchResult.uom }}</span>
                        </div>
                        <div class="right-panel">
                            <span v-if="searchResult.showButton">
                                <i
                                    class="icon-vansalesreturn"
                                    @click="showPopup(searchResult)"
                                ></i
                            ></span>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
import Vue from "vue";

Vue.component("bo-toast", require("@/v2/components/bo-toast.vue").default);
Vue.component("bo-search-history", require("js/frontend/components/bo-search-history.vue").default);

import productMixin from "@/v2/mixins/products.js";
import documentsMixin from "@/v2/mixins/documents.js";
import { searchPreferenceMixin } from "js/frontend/mixins/search-preference.js";
import { plainText } from "js/frontend/utils/search-text.js";

export default {
    mixins: [productMixin, documentsMixin, searchPreferenceMixin],
    props: {
        productsUrl: {
            type: String,
            default: null,
        },
        returnCatalogPage: {
            type: String,
            default: null,
        },
        companyId: {
            type: Number,
            default: null,
        },
        isSerialNumberEnable: {
            type: Boolean,
            default: false,
        },
        salesReturnValidationMode: {
            type: String,
            default: null,
        },
        generalTranslations: {
            type: Object,
            default: null,
        },
        headerTranslations: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            searchValue: "",
            previousSearchValue: "",
            categoryResults: [],
            serialNumberResults: [],
            productResults: [],
            showSearch: false,
            vcoConfig: {
                handler: this.deactivateIfNeeded,
                middleware: this.ignoreLoadingElement,
            },
            showResult: false,
        };
    },

    mounted() {
        this.$refs.dropdownSearchBar.addEventListener("click", (event) => {
            event.stopPropagation();
        });

        // Bootstrap closes this dropdown itself
        window.$?.(this.$el).on("hide.bs.dropdown", () => (this.editingSearchHistory = false));
    },

    computed: {
        // a serial number hit replaces the plain product row for the same product
        searchResults() {
            const serialProductIds = this.serialNumberResults.map((result) => result.id);

            return [
                ...this.categoryResults,
                ...this.serialNumberResults,
                ...this.productResults.filter((result) => !serialProductIds.includes(result.id)),
            ];
        },
    },

    methods: {
        getSearchResult() {
            const search = this.searchValue.trim();
            const isCurrentSearch = () => search === this.searchValue.trim();

            this.showResult = false;
            this.categoryResults = [];
            this.serialNumberResults = [];
            this.productResults = [];

            if (this.productFields.length > 0) {
                const payload = {
                    params: {
                        luceneSearchString: search,
                        luceneSearchField: this.productFields,
                        isReturnModeSearch: true,
                        limit: 10,
                    },
                };
                this.getProducts(
                    this.productsUrl,
                    payload,
                    (products) => {
                        if (isCurrentSearch()) {
                            this.productResults = products.map(this.convertProductToSearchResult);
                            this.showResult = true;
                        }
                    },
                    null,
                    false
                );
            }

            if (this.isSerialNumberEnable) {
                let lineItemPayload = {
                    params: {
                        serial_number: search,
                        company_id: this.companyId,
                        limit: 1000,
                    },
                };
                if (this.salesReturnValidationMode == "document_binding") {
                    lineItemPayload["params"]["serial_number_not_transfered"] = search;
                }
                this.getDocumentLineItems(lineItemPayload, (lineItems) => {
                    if (isCurrentSearch()) {
                        this.serialNumberResults = this.convertLineItemsToSearchResults(lineItems, search);
                        this.showResult = true;
                    }
                });
            }

            if (this.searchCategories) {
                this.searchCategoryEntries({ search, isReturnMode: true }, (categories) => {
                    if (isCurrentSearch()) {
                        this.categoryResults = categories.map(this.convertCategoryToSearchResult);
                        this.showResult = true;
                    }
                });
            }
        },

        convertProductToSearchResult(product) {
            return {
                key: `product-${product.id}`,
                id: product.id,
                type: this.headerTranslations?.product ?? "product",
                name: product.name,
                sku: product.sku,
                barcode: product.barcode,
                description: plainText(product.description),
                uom: product.uom ? product.uom.name : null,
                showButton: true,
                item: product,
            };
        },

        convertLineItemsToSearchResults(lineItems, serialNumber) {
            const results = {};

            Object.values(lineItems).forEach((lineItem) => {
                results[lineItem.product.id] = {
                    key: `document-${lineItem.product.id}`,
                    id: lineItem.product.id,
                    type: "document",
                    name: lineItem.product_name,
                    showButton: true,
                    sku: lineItem.product_sku,
                    uom: lineItem.product.uom.name,
                    serial_number: serialNumber,
                    item: lineItem,
                };
            });

            return Object.values(results);
        },

        convertCategoryToSearchResult(category) {
            return {
                key: `category-${category.id}`,
                id: category.id,
                isCategory: true,
                type: this.headerTranslations?.category ?? "category",
                name: (category.full_name ?? category.name).split("->").join(" › "),
                showButton: false,
                item: category,
            };
        },

        async clickResult(searchResult) {
            if (searchResult.isCategory) {
                await Promise.all([this.waitForSearchPreferenceSave(), this.recordSearch(this.searchValue)]);
                window.location.assign(`${this.returnCatalogPage}/category/${searchResult.item.id}`);
            } else {
                this.recordSearch(this.searchValue);
                this.showPopup(searchResult);
            }
        },

        // no results page here: a history chip runs the dropdown search
        searchHistoryTerm(term) {
            this.searchValue = term;
            this.recordSearch(term);
            this.searchIfNeeded();
        },

        showPopup(searchResult) {
            this.deactivateIfNeeded();
            this.$emit("active_return_popup", searchResult);
        },

        searchIfNeeded() {
            if (this.searchValue.trim() != "") {
                if (this.previousSearchValue != this.searchValue) {
                    this.getSearchResult();
                    this.previousSearchValue = this.searchValue;
                }
            }
        },

        deactivateIfNeeded() {
            this.showSearch = false;
        },

        ignoreLoadingElement(event) {
            if (event.target.classList.contains("loading")) return false;
            return true;
        },

        checkKeyPress(event) {
            if (event.key === "Enter") {
                this.recordSearch(this.searchValue);
                this.searchIfNeeded();
            }
        },

        filterControl(field) {
            if (this.toggleSearchField(field) && this.searchValue.trim() != "") {
                this.getSearchResult();
            }
        },
    },
};
</script>
